import React from "react";
import { compose } from "recompose";
import { withSnackbar } from "notistack";
import styles from "../styles";
import { withStyles } from "@material-ui/core";
import { postData } from "../../../../../../api/componentAPIs";
import constants from "./constants";
import appConstants from "../../../../../../appConstants";
import ShipmentsModel from "./components/ShipmentsModel";
import Events from "../../../../../../utilities/EventEmitter";
import { RMAXLogger } from "../../../../../../services/LoggerService/RMAXLogger";
import { getMoveStatus, getShipmentsInTrailer } from "./utils";
import { Drop_Hook_Move_Page_Names } from "../../constants";
import MoveBills from "./components/MoveBills";
import { isEqual } from "lodash/lang";
import { withAppConfig } from "../../../../../../appConfigContext";
import { getEquipmentSuffix } from "../../../utils/utils";
import { getTrailerOptions } from "../utils";

class MoveBillsContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      fromTrailer: null,
      movedToTrailerNumber: null,
      movedToTrailerScac: null,
      selectedStopType: null,
      selectedShipments: [],
      errorInfo: this.getDefaultErrorInfo(),
      moveBillError: null,
      moveBillsDelayed: null,
      isMoveBillsInProgress: false,
      fromTrailersList: [],
      toTrailersList: [],
    };
    this.timer = null;
  }

  componentDidMount() {
    Events.on("MoveBills", this.checkMoveBillsStatus);
    RMAXLogger.info("Move_Bills", { action: "Viewed" });
    this.generateDataOptions();
  }

  componentDidUpdate(prevProps) {
    if (
      !isEqual(prevProps.routeData, this.props.routeData) ||
      this.props.trailerList !== prevProps.trailerList
    ) {
      this.generateDataOptions();
    }
  }

  componentWillUnmount() {
    Events.removeListener("MoveBills", this.checkMoveBillsStatus);
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  onMoveBillsProgressTimeOut = () => {
    this.timer = setTimeout(() => {
      this.setState({ moveBillsDelayed: true });
    }, appConstants.ONE_MINUTE_TIME_OUT);
  };

  checkMoveBillsStatus = (data) => {
    const {
      success,
      message,
      driverId,
      movedToTrailerNumber,
      movedToTrailerScac,
    } = data;

    if (this.props.routeData.driverId?.toUpperCase() !== driverId?.toUpperCase()) {
      return;
    }
    if (success) {
      const showTrailerScac= this.props.configJSON.showTrailerScac;
      const movedFromTrailer = this.state.fromTrailer.label;
      const movedToTrailer = movedToTrailerNumber;
      const fromTrailerScac = getEquipmentSuffix({
        equipmentScac: this.state.fromTrailer.trailerScac,
        showTrailerScac,
      })
      const toTrailerScac = getEquipmentSuffix({
        equipmentScac: movedToTrailerScac,
        showTrailerScac,
      })

      this.props.enqueueSnackbar(
        `Bills moved successfully from trailer number ${movedFromTrailer} ${fromTrailerScac} to ${movedToTrailer} ${toTrailerScac}`,
        {
          variant: "success",
        },
      );
      this.props.onClose();
    } else {
      this.props.enqueueSnackbar(`Move Bills Failed`, {
        variant: "error",
      });
      this.setState({
        isMoveBillsInProgress: false,
        moveBillsDelayed: false,
        moveBillsError: message.error,
      });
    }
    clearTimeout(this.timer);
  };

  getDefaultErrorInfo() {
    return {
      fromTrailer: null,
      movedToTrailerNumber: null,
      selectedStopType: null,
    };
  }

  generateDataOptions = () => {
    const { trailerList, currentDriverRoutes } = this.props;
    const fromTrailersList = currentDriverRoutes
      .filter((trailer) => Boolean(trailer.dispatchId))
      .map(({ trailerNumber, trailerId, trailerScac, isActiveRoute }) => ({
        label: trailerNumber,
        value: trailerNumber,
        isActiveRoute,
        trailerId,
        trailerScac,
        labelSuffix: getEquipmentSuffix({
          equipmentScac: trailerScac,
          showTrailerScac: this.props.configJSON.showTrailerScac,
        }),
      }));

    const toTrailersList = getTrailerOptions(trailerList, this.props.configJSON.showTrailerScac);
    this.setState({
      fromTrailersList,
      toTrailersList,
    });
  };

  handleChange = (field) => (value) => {
    this.setState((prevState) => ({
      ...prevState,
      [field]: value,
      ...(field === "movedToTrailerNumber" && {
        movedToTrailerScac: value?.trailerScac ?? null,
      }),
      errorInfo: {
        ...prevState.errorInfo,
        [field]: this.getFieldError(field, value),
      },
      moveBillError: null,
    }));
  };

  handleBillType = (value) => {
    this.setState(
      (state) => ({
        selectedStopType: value,
        errorInfo: {
          ...state.errorInfo,
          selectedStopType: this.getFieldError("selectedStopType", value),
        },
        moveBillError: null,
      }),
      this.selectStops,
    );
  };

  selectStops = () => {
    const { selectedStopType, fromTrailer } = this.state;
    if (!Boolean(selectedStopType)) {
      return;
    }
    const {
      allShipments,
      allDeliveryShipments,
      allPickedUpShipments,
      individualSelectedShipments,
    } = getShipmentsInTrailer(fromTrailer, this.props.currentDriverRoutes);
    const {
      STOPS: { ALL_STOPS, ALL_DELIVERIES, ALL_PICKUPS, SELECT_STOPS },
    } = constants;
    switch (selectedStopType.value) {
      case ALL_STOPS:
        this.setSelectedShipments([...allShipments]);
        break;
      case ALL_DELIVERIES:
        this.setSelectedShipments([...allDeliveryShipments]);
        break;
      case ALL_PICKUPS:
        this.setSelectedShipments([...allPickedUpShipments]);
        break;
      case SELECT_STOPS:
        this.setSelectedShipments([...individualSelectedShipments]);
        break;
    }
  };

  setSelectedShipments = (shipments) => {
    this.setState({
      selectedShipments: shipments,
    });
  };

  onShipmentSelected = (shipmentId) => () => {
    const { selectedShipments } = this.state;
    const newChecked = selectedShipments.map((bill) => {
      if (bill.shipment.shipmentId === shipmentId) {
        return {
          ...bill,
          isChecked: !bill.isChecked,
        };
      }
      return bill;
    });

    this.setState({
      selectedShipments: newChecked,
    });
  };

  getProDetails = () => {
    const { selectedShipments, fromTrailer } = this.state;
    let deliveryPros = [],
      returnPros = [],
      pickupPros = [];
    const movedFromRouteId = this.props.currentDriverRoutes.find(
      (route) => route.trailerId === this.state.fromTrailer?.trailerId
    ).routeId;
    selectedShipments
      .filter((bill) => bill.isChecked)
      .forEach((bill) => {
        if (bill.stop.stopType === appConstants.STOP_TYPE.Pickup) {
          pickupPros.push({
            pickupId: bill.shipment.pickupId,
            proNumber: bill.shipment.proId,
          });
        } else if (bill.stop.stopType === appConstants.STOP_TYPE.Delivery) {
          const proObj = {
            shipmentId: bill.shipment.shipmentId,
            proNumber: bill.shipment.proId,
          };
          if (bill.shipment?.deliveryCode === constants.RETURN_DELIVERY_CODE) {
            return returnPros.push(proObj);
          }
          deliveryPros.push(proObj);
        }
      });
    const moveStatus = getMoveStatus({
      deliveryPros,
      returnPros,
      pickupPros,
      fromTrailer,
      currentDriverRoutes: this.props.currentDriverRoutes,
    });
    return {
      deliveryPros,
      returnPros,
      pickupPros,
      movedFromRouteId,
      moveStatus,
    };
  };

  handleSaveBills = () => {
    const errorInfo = this.getErrorInfo();
    const error = Object.values(errorInfo).filter((value) => value).length;
    if (!error) {
      const {
        deliveryPros,
        returnPros,
        pickupPros,
        movedFromRouteId,
        moveStatus,
      } = this.getProDetails();
      if (!(deliveryPros.length || returnPros.length || pickupPros.length)) {
        this.setState({
          moveBillError: constants.MESSAGES.BILL_REQUIRED,
        });
        return;
      }
      const inputObj = {
        terminalCode: this.props.userProfile.terminal.code,
        driverId: this.props.routeData.driverId,
        movedFromRouteId: movedFromRouteId,
        movedToTrailerNumber: this.state.movedToTrailerNumber?.value,
        movedToTrailerScac: this.state.movedToTrailerScac,
        deliveryPros: deliveryPros,
        returnPros: returnPros,
        pickupPros: pickupPros,
        moveStatus: moveStatus,
      };
      this.setState(
        {
          isMoveBillsInProgress: true,
          moveBillsError: null,
        },
        this.onMoveBillsProgressTimeOut,
      );
      RMAXLogger.info("Move_Bills", {
        action: "Move_Bills Clicked",
        payload: inputObj,
      });
      postData("MoveBills", inputObj)
        .then((response) => {
          if (response && Math.trunc(response.status / 100) === 2);
          else {
            this.handleMoveBillsError(response);
          }
        })
        .catch(() => {
          this.handleMoveBillsError();
        });
    }
  };

  handleNextClick = () => {
    const errorInfo = this.getErrorInfo();
    const error = Object.values(errorInfo).filter((value) => value).length;
    if (!error) {
      this.props.setPageName(Drop_Hook_Move_Page_Names.MOVE_BILLS);
    }
  };

  handleMoveBillsError = (response) => {
    clearTimeout(this.timer);
    this.setState({
      isMoveBillsInProgress: false,
      moveBillsDelayed: null
    });
    if (response?.status === 403) {
      return;
    }
    const apiErrorMsg =
      response?.response?.data?.error?.message || response?.response?.data?.error?.details;
    this.props.enqueueSnackbar(apiErrorMsg || `Error while processing Move Bills`, {
      variant: "error",
    });
  };

  getErrorInfo = () => {
    let errorInfo = {};
    Object.keys(this.state.errorInfo).forEach((fieldName) => {
      errorInfo[fieldName] = this.getFieldError(
        fieldName,
        this.state[fieldName],
      );
    });
    this.setState({
      errorInfo,
    });
    return errorInfo;
  };

  getMovedToTrailerError = (value) => {
    if (
      this.props.allowedTrailerStatusesToMoveBills.includes(value.trailerStatus)
    ) {
      return null;
    }
    return `Valid trailer status: ${
      this.props.allowedTrailerStatusesToMoveBills.length === 0
        ? 'None'
        : this.props.allowedTrailerStatusesToMoveBills.join(', ')
    }`;
  };

  getFieldError = (field, value) => {
    switch (field) {
      case "movedToTrailerNumber":
        return value?.label?.trim()
          ? this.getMovedToTrailerError(value)
          : constants.MESSAGES.REQUIRED;
      case "selectedStopType":
        return value?.label?.trim() ? null : constants.MESSAGES.REQUIRED;
      default:
        return null;
    }
  };

  render() {
    const { isSelectShipmentPage, onClose, isSectionDisabled, routeData } =
      this.props;
    const {
      errorInfo,
      selectedStopType,
      selectedShipments,
      movedToTrailerNumber,
      isMoveBillsInProgress,
      moveBillsDelayed,
      moveBillError,
      fromTrailer,
    } = this.state;
    return (
      <>
        {isSelectShipmentPage ? (
          <ShipmentsModel
            data={selectedShipments}
            onShipmentSelected={this.onShipmentSelected}
            errorInfo={errorInfo}
            toTrailersList={this.state.toTrailersList}
            handleChange={this.handleChange}
            fromTrailer={fromTrailer}
            movedToTrailerNumber={movedToTrailerNumber}
            handleSaveBills={this.handleSaveBills}
            isMoveBillsInProgress={isMoveBillsInProgress}
            moveBillsDelayed={moveBillsDelayed}
            moveBillError={moveBillError}
            onClose={onClose}
          />
        ) : (
          <MoveBills
            fromTrailersList={this.state.fromTrailersList}
            toTrailersList={this.state.toTrailersList}
            routeData={routeData}
            handleChange={this.handleChange}
            errorInfo={errorInfo}
            fromTrailer={fromTrailer}
            movedToTrailerNumber={movedToTrailerNumber}
            selectedStopType={selectedStopType}
            handleBillType={this.handleBillType}
            handleNextClick={this.handleNextClick}
            isSectionDisabled={isSectionDisabled}
          />
        )}
      </>
    );
  }
}

export default compose(
  withStyles(styles),
  withSnackbar,
  withAppConfig
)(MoveBillsContainer);
